import { Box, Button, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useRouter } from "next/router";
import { useUser } from "@/auth/useUser";
import { DATE_FORMATS } from "@/config";
import { useIsOTPClientPage } from "@/hooks/client/useIsOTPClientPage";
import { InvoiceStatus } from "@/types";
import InvoiceStatusChip from "../../invoice/common/invoiceStatusChip";

export type Invoice = {
  id: string;
  status: string;
  created: string;
  uuid: string;
  priceSummary?: {
    total?: string;
  };
};

type Props = {
  invoice: Invoice;
};

const EMPTY_DATE = "No date";

export default function InvoiceTile({ invoice }: Props) {
  const { medspa, hasUrlPermission, newPermissionsEnabledForUser } = useUser();
  const { query } = useRouter();
  const { medspaSlug } = query;

  const isOTPClientPage = useIsOTPClientPage();

  const createdDate =
    invoice.status === InvoiceStatus.DRAFT
      ? EMPTY_DATE
      : format(parseISO(invoice.created), DATE_FORMATS.DATE_PICKER);

  const href = isOTPClientPage
    ? `/client/${medspaSlug}/receipt/${invoice.id}`
    : `/${medspa}/invoices/${invoice.id}`;

  const canSeeInvoiceDetails = newPermissionsEnabledForUser
    ? hasUrlPermission(`/${medspa}/invoices/${invoice.id}`)
    : true;

  return (
    <Button
      href={href}
      disabled={!canSeeInvoiceDetails}
      target={isOTPClientPage ? "_blank" : "_self"}
      aria-label={`Invoice #${invoice.uuid} details`}
      sx={{
        py: 3,
        px: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", mb: 0.5 }}>
        <DateDisplay date={createdDate} showStatus status={invoice.status} />
      </Box>
      <Typography
        variant="paragraphSmall"
        sx={{
          color: "secondary.main",
        }}
      >
        ${invoice.priceSummary?.total || 0} • No. {invoice.uuid}
      </Typography>
    </Button>
  );
}

type DateDisplayProps = {
  label?: string;
  date: string;
  showStatus?: boolean;
  status?: string;
};

const DateDisplay = ({ label, date, showStatus, status }: DateDisplayProps) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    {label && (
      <Typography
        variant="paragraphSmall"
        sx={{
          color: "text.secondary",
          mr: 1,
        }}
      >
        {label}
      </Typography>
    )}
    <Typography
      variant={date === EMPTY_DATE ? "paragraph" : "h3"}
      sx={{
        color: "text.primary",
        mr: 1,
        fontStyle: date === EMPTY_DATE ? "italic" : undefined,
        wordBreak: "break-word",
      }}
    >
      {date}
    </Typography>
    {showStatus && status && (
      <InvoiceStatusChip status={status as InvoiceStatus} />
    )}
  </Box>
);
